import { create } from "zustand";
import {
  baselineOf,
  isEmptyDraft,
  shouldRestore as coreShouldRestore,
  wipeDraft,
  type EditorDraft,
} from "./draft-core";
import { type ItemContent } from "./items";

// Survival store for the item editor. `ItemEditor`'s form state used to live only in component
// `useState`, so ANY unmount of AppShell threw the half-typed item away — and there are several:
// the auto-lock on tab-hide/idle (ui/useIdleLock.ts), a 401 bouncing the app to <Login/>
// (ui/ApiErrorPolicy.tsx), or the vault selection changing under the dialog (ui/DialogHost.tsx).
// Because this store lives at module level it OUTLIVES those unmounts, so the editor can be
// restored (ui/DraftRestoreBanner.tsx) instead of retyped.
//
// SECURITY (§8): a draft holds PLAINTEXT secrets, so there is deliberately NO zustand `persist`
// middleware and no localStorage/sessionStorage/IndexedDB/server write anywhere in this file — the
// org key is gone while the vault is locked, so encrypting-at-rest is not solvable without weakening
// the threat model. Memory only. It is dropped on sign-out, on a different user signing in, on an
// explicit discard, after PARK_TTL_MS, and (unavoidably) on a page reload. It is deliberately NOT
// dropped by vault.lock() or by useAuth.clear() — surviving exactly those two is the whole point.

export type ItemDraft = EditorDraft<ItemContent>;

interface DraftState {
  /** The edit currently open in the editor. */
  draft: ItemDraft | null;
  /** Whether `draft` differs from what the editor opened with. */
  dirty: boolean;
  /** An edit whose editor went away with unsaved content, waiting to be restored. */
  parked: ItemDraft | null;
  /** Editor changed: mirror the current form state and whether it still differs from the baseline. */
  update: (d: ItemDraft, dirty: boolean) => void;
  /** The editor adopted the parked draft — it must not be offered a second time. */
  consumeParked: () => void;
  /** Editor unmounted: keep the draft iff it has unsaved content worth restoring. */
  park: () => void;
  /** Non-consuming, side-effect-free read (an expired entry reads as absent). */
  peek: () => ItemDraft | null;
  /** Saved, or the user confirmed a discard: forget everything. */
  clear: () => void;
  /** Sign-out / another user: scrub the plaintext too. */
  wipe: () => void;
}

export const useDraft = create<DraftState>((set, get) => ({
  draft: null,
  dirty: false,
  parked: null,

  update: (d, dirty) => set({ draft: d, dirty }),

  consumeParked: () => set({ parked: null }),

  park: () => {
    const { draft, dirty } = get();
    if (!draft || !dirty || isEmptyDraft(draft)) {
      set({ draft: null, dirty: false });
      return;
    }
    set({ parked: { ...draft, parkedAt: Date.now() }, draft: null, dirty: false });
  },

  // Pure on purpose: ItemEditor consults this from a useState initialiser (i.e. during render), so
  // it must not write to the store. Expiry is enforced by DraftRestoreBanner's timer.
  peek: () => {
    const p = get().parked;
    return p && coreShouldRestore(p, { orgId: p.orgId, itemId: p.itemId }, Date.now()) ? p : null;
  },

  clear: () => set({ draft: null, dirty: false, parked: null }),

  wipe: () => {
    const { draft, parked } = get();
    wipeDraft(draft);
    wipeDraft(parked);
    set({ draft: null, dirty: false, parked: null });
  },
}));

/** Would a draft be offered to an editor opening on this target? Used by ItemEditor's initialiser. */
export function restorableDraft(want: { orgId: string; itemId?: string | null }): ItemDraft | null {
  const p = useDraft.getState().peek();
  return p && coreShouldRestore(p, want, Date.now()) ? p : null;
}

export { baselineOf };
